/**
 * The words the app gives the API's codes, as the current app's lang/pt.js has
 * them, and the rules that read an event's state. Pure: usable from the server
 * page and the client components alike.
 */
import { eventHasEnded, wallClock } from "@/lib/eventTime";
import type { Difficulty, EventDetails, EventType, RouteType, ScheduleEntryType, Weather } from "@/lib/types/event";

// the six grades of difficulty, easiest first: three levels of two grades each
export const DIFFICULTIES: Difficulty[] = ["EASY_1", "EASY_2", "MODERATE_1", "MODERATE_2", "HARD_1", "HARD_2"];

export const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  EASY_1: "Fácil I",
  EASY_2: "Fácil II",
  MODERATE_1: "Moderado I",
  MODERATE_2: "Moderado II",
  HARD_1: "Difícil I",
  HARD_2: "Difícil II",
};

/** the colour of each step of the difficulty bar, green to red */
export const DIFFICULTY_COLOURS = ["#7ccf98", "#34a05a", "#f6d05a", "#e3a21f", "#ef7b72", "#cc3530"];

export const isDifficulty = (value: unknown): value is Difficulty => DIFFICULTIES.includes(value as Difficulty);

/** 1..6 for a grade, 0 for anything else */
export const difficultyGrade = (difficulty: unknown) => DIFFICULTIES.indexOf(difficulty as Difficulty) + 1;

export const difficultyLabel = (difficulty: unknown) => (isDifficulty(difficulty) ? DIFFICULTY_LABELS[difficulty] : "");

export const EVENT_TYPES: Record<EventType, string> = {
  HIKE: "Caminhada",
  CAMPING: "Acampamento",
  CIRCLE: "Círculo Aberto",
  PARTY: "Festa",
  SOIREE: "Serão",
  SALON: "Tertúlia",
};

export const eventTypeLabel = (type: string | null | undefined) => (type && EVENT_TYPES[type as EventType]) || type || "";

export const ROUTE_TYPES: Record<RouteType, string> = {
  LOOP: "Circular",
  LINEAR: "Linear",
  OUT_AND_BACK: "Ida e volta",
  STACKED_LOOP: "Vários circuitos",
};

export const routeTypeLabel = (type: string | null | undefined) => (type && ROUTE_TYPES[type as RouteType]) || type || "";

export const REGISTRATION_LABELS: Record<string, string> = {
  OPEN: "Inscrições abertas",
  WAITING: "Inscrições em breve",
  CLOSED: "Inscrições fechadas",
};

/** what each kind of programme entry is called; the ones that point at a row are titled by it */
export const SCHEDULE_ENTRY_LABELS: Record<ScheduleEntryType, string> = {
  START: "Início",
  END: "Fim",
  MEETING_POINT: "Ponto de encontro",
  TEXT: "",
  PICNIC: "Piquenique",
  LUNCH: "Almoço",
  DINNER: "Jantar",
  POI: "",
  TRAIL: "",
  ACCOMMODATION: "",
};

export const WEATHER_WORDS: Record<Weather, string> = {
  SNOW: "neve",
  RAIN: "chuva",
  COLD: "frio",
  CLOUDY: "céu nublado",
  SUNNY: "sol",
  WARM: "calor",
  HOT: "muito calor",
};

/** "A", "A e B", "A, B e C" */
export const listWords = (words: string[]) => (words.length > 1 ? `${words.slice(0, -1).join(", ")} e ${words[words.length - 1]}` : words[0] || "");

/**
 * Whether an event is over: at the later of its start and its end, so an end
 * entered before the start does not end it early.
 */
export const eventEnded = (event: Pick<EventDetails, "start_time" | "end_time">) => {
  const end = wallClock(event.end_time) > wallClock(event.start_time) ? event.end_time : event.start_time;
  return eventHasEnded({ end_time: end, start_time: event.start_time });
};

/** whether an event has no places left for this visitor: full, and they are not already going */
export const isSoldOutFor = (event: { availability?: string | null; is_registered?: unknown } | null | undefined) =>
  !!event && event.availability === "SOLD_OUT" && !event.is_registered;

/** a client registered and not still to pay: the one with a view of their own */
export const isGoing = (event: { is_registered?: unknown; awaiting_payment?: unknown } | null | undefined) =>
  !!event && !!event.is_registered && !event.awaiting_payment;

/** 3 -> "3h", 3.5 -> "3h30", 0.75 -> "45 min"; nothing for none */
export const durationText = (hours: number | string | null | undefined) => {
  const value = Number(hours);
  if (!Number.isFinite(value) || value <= 0) {
    return "";
  }
  const minutes = Math.round(value * 60);
  const whole = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!whole) {
    return `${rest} min`;
  }
  return rest ? `${whole}h${String(rest).padStart(2, "0")}` : `${whole}h`;
};

/** "1 foto", "8 fotos" */
export const photoCount = (count: number) => `${count} ${count === 1 ? "foto" : "fotos"}`;

/**
 * A trail's name as an event walks it: with the variant's name beside it when the event
 * walks one, since the same trail then shows other figures than on an event that walks
 * its main route, and nothing else would say why.
 */
export function trailTitle(trail: { title: string | null; variant?: { title: string } | null }): string {
  const title = String(trail.title || "").trim();
  const variant = String(trail.variant?.title || "").trim();
  return variant ? (title ? `${title} (${variant})` : variant) : title;
}
